// NIGHT-DIRECTSTAFF-2026-10-22 §8/§9: credentials with expiry dates, and "Expiring soon".
//
// NO NEW TABLE (the safer reading, stated): there is no `credentials` table; the portal's onboarding already stores licenses
// and CPR cards in public.onboarding_documents (kind, license_state, expires_on, the file in the private 'onboarding-docs'
// bucket, staff-allowlist RLS -- the packet's own access rules). The CRM reads those and adds to them; nothing is copied.
// The fields the onboarding does not have -- the number, the issue date, the name of a certification, and the kinds
// 'certification' (BLS / ACLS / PALS...) and 'compact_privilege' -- come with the hub's additive
// 2026-10-22-onboarding-documents-credentials.sql; until it is applied the CRM probes once and offers License and CPR card
// only, with no number or issue date (said). The onboarding_expirations view (pt-agents' expiry reminders) is NOT changed.
//
// §9's list is machine-readable for the credential-watch agent: { candidate_id, kind, state, expires_on, days_left, window }
// with window 60 | 30 | 14 (the tightest one it is inside).
export const CRED_KINDS = { license: 'License', cpr: 'CPR / BLS card', certification: 'Certification', compact_privilege: 'Compact privilege' };
export const BASE_KINDS = ['license', 'cpr'];
export const EXTRA_COLUMNS = ['credential_number', 'issued_on', 'credential_name'];
export const WINDOWS = [14, 30, 60];
export const CRED_LOG_TYPE = 'credential';
export const FILE_TYPES = { 'application/pdf': 'pdf', 'image/jpeg': 'jpg', 'image/png': 'png', 'image/heic': 'heic', 'image/webp': 'webp' };
export const FILE_MAX = 10 * 1024 * 1024;
export const DARK_WHY = 'Number, issue date, certifications and compact privileges turn on when the hub applies the credentials update (2026-10-22-onboarding-documents-credentials.sql).';
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const dayStart = (now) => Date.UTC(new Date(now).getUTCFullYear(), new Date(now).getUTCMonth(), new Date(now).getUTCDate());
export const daysLeftOf = (date, now = Date.now()) => (DATE.test(s(date)) ? Math.round((Date.parse(`${s(date)}T00:00:00Z`) - dayStart(now)) / 864e5) : null);
export const windowOf = (days) => (days === null || days < 0 ? null : WINDOWS.find((w) => days <= w) || null);

/** "••••4821" -- the number masked to its last four (shown whole only on tap). */
export const maskNumber = (n) => { const x = s(n); return !x ? '' : x.length <= 4 ? '••••' : `••••${x.slice(-4)}`; };
export const credLabel = (d) => `${s(d.credential_name) || CRED_KINDS[d.kind] || d.kind}${d.license_state ? ` (${d.license_state})` : ''}`;

/** The current credentials: the newest row per kind / state / name (older rows are the renewal history). */
export function currentCredentials(rows = []) {
  const kinds = new Set(Object.keys(CRED_KINDS));
  const best = new Map();
  for (const d of rows) {
    if (!kinds.has(d.kind)) continue;
    const k = `${d.candidate_id}|${d.kind}|${s(d.license_state)}|${s(d.credential_name).toLowerCase()}`;
    const cur = best.get(k);
    if (!cur || s(d.uploaded_at) > s(cur.uploaded_at)) best.set(k, d);
  }
  return [...best.values()].sort((a, b) => s(a.expires_on || '9999').localeCompare(s(b.expires_on || '9999')));
}

/** Form -> { row, errors }. extra: the hub's columns exist. */
export function validateCredential(f, { extra = false, now = Date.now() } = {}) {
  const errors = {};
  const kind = s(f.kind);
  const allowed = extra ? Object.keys(CRED_KINDS) : BASE_KINDS;
  if (!allowed.includes(kind)) errors.kind = extra ? 'Pick a type.' : 'Only a license or a CPR card until the credentials update is applied.';
  const st = s(f.license_state).toUpperCase();
  if ((kind === 'license' || kind === 'compact_privilege') && !/^[A-Z]{2}$/.test(st)) errors.license_state = 'The state, two letters (like TX).';
  else if (st && !/^[A-Z]{2}$/.test(st)) errors.license_state = 'Two letters, like TX.';
  if (!DATE.test(s(f.expires_on))) errors.expires_on = 'The expiry date.';
  else if (daysLeftOf(f.expires_on, now) < 0) errors.expires_on = 'That date has passed. Add the current card or license.';
  if (extra) {
    if (s(f.credential_number).length > 40) errors.credential_number = 'Too long.';
    if (s(f.issued_on) && !DATE.test(s(f.issued_on))) errors.issued_on = 'Not a date.';
    if (s(f.issued_on) && DATE.test(s(f.expires_on)) && s(f.issued_on) > s(f.expires_on)) errors.issued_on = 'Issued after it expires?';
    if (kind === 'certification' && !s(f.credential_name)) errors.credential_name = 'Which certification (BLS, ACLS, PALS...)?';
    if (s(f.credential_name).length > 80) errors.credential_name = 'Too long.';
  }
  if (f.file) {
    if (!FILE_TYPES[f.file.type]) errors.file = 'A PDF or a photo (JPG, PNG, HEIC, WebP).';
    else if (f.file.size > FILE_MAX) errors.file = 'Over 10 MB.';
  } else errors.file = 'Attach the card or license (a PDF or a photo).';
  const row = { kind, license_state: st || null, expires_on: s(f.expires_on) };
  if (extra) Object.assign(row, { credential_number: s(f.credential_number) || null, issued_on: s(f.issued_on) || null, credential_name: s(f.credential_name) || null });
  return { row, errors };
}

/** §9: what is expiring within 60 days, tightest first. Test candidates out. -> machine-readable rows (+ the candidate) */
export function expiringSoon({ documents = [], candidates = [], now = Date.now() } = {}) {
  const byId = new Map((candidates || []).map((c) => [String(c.id), c]));
  const out = [];
  for (const d of currentCredentials(documents)) {
    const days = daysLeftOf(d.expires_on, now);
    const w = windowOf(days);
    if (!w) continue;
    const c = byId.get(String(d.candidate_id));
    if (!c || (Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test'))) continue;
    out.push({ candidate_id: d.candidate_id, document_id: d.id, kind: d.kind, state: d.license_state || null, name: s(d.credential_name) || null,
      expires_on: d.expires_on, days_left: days, window: w, label: credLabel(d), candidate: c });
  }
  return out.sort((a, b) => a.days_left - b.days_left);
}

/** The reminder (no link: it asks them to reply with the renewal, as the expiry-reminder drafts do). */
export function expiryMessage(name, items) {
  const f = s(name).split(/\s+/)[0];
  const hi = /^[A-Za-z][A-Za-z'.-]*$/.test(f) ? `Hi ${f},` : 'Hi,';
  const when = (x) => new Date(`${x.expires_on}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const list = items.map((x) => `your ${x.label} expires ${when(x)}`).join(', and ');
  return `${hi} a quick reminder from ProTherapy Staffing: ${list}. When you have the renewal, reply with a photo or PDF of it and we'll update your file.`;
}

/** Is the hub's credentials update applied? */
export async function probeCredentialColumns(client) {
  try { const { error } = await client.from('onboarding_documents').select(EXTRA_COLUMNS.join(','), { head: true }).limit(0); return !error; } catch { return false; }
}

const COLS = (extra) => `id,candidate_id,kind,license_state,expires_on,uploaded_at,uploaded_by,storage_path${extra ? `,${EXTRA_COLUMNS.join(',')}` : ''}`;
export function makeCredentialsApi({ supabase }) {
  const whoAmI = async () => { try { const { data } = await supabase.auth.getUser(); return (data && data.user && data.user.email) || ''; } catch { return ''; } };
  return {
    /** all credential rows (every candidate, or one) -> rows | null (not readable: the allowlist) */
    async list(extra, candidateId = null) {
      let q = supabase.from('onboarding_documents').select(COLS(extra)).in('kind', extra ? Object.keys(CRED_KINDS) : BASE_KINDS);
      if (candidateId !== null) q = q.eq('candidate_id', candidateId);
      const { data, error } = await q;
      return error ? null : (data || []);
    },
    /** a new credential row (a renewal is a new row: the old one stays as history) with its file in the private bucket */
    async add(candidateId, form, { extra = false, renewal = false, now = new Date() } = {}) {
      const { row, errors } = validateCredential(form, { extra, now: now.getTime() });
      if (Object.keys(errors).length) { const e = new Error(Object.values(errors)[0]); e.errors = errors; throw e; }
      const by = await whoAmI();
      const path = `${candidateId}/credential/${now.getTime()}-${row.kind}.${FILE_TYPES[form.file.type]}`;
      const up = await supabase.storage.from('onboarding-docs').upload(path, form.file, { upsert: false, contentType: form.file.type });
      if (up.error) throw new Error(`The file could not be stored: ${up.error.message}`);
      const ins = await supabase.from('onboarding_documents').insert({ ...row, candidate_id: candidateId, storage_path: path, uploaded_by: by ? `staff ${by}` : 'staff' }).select('id');
      if (ins.error) { await supabase.storage.from('onboarding-docs').remove([path]).catch(() => {}); throw new Error(`Not saved: ${ins.error.message}`); }
      const note = `Credential ${renewal ? 'renewed' : 'added'} · ${credLabel(row)} · expires ${row.expires_on}${by ? ` · by ${by}` : ''}`;
      await supabase.from('activity_log').insert({ candidate_id: candidateId, date: now.toISOString().slice(0, 10), type: CRED_LOG_TYPE, note });
      return (ins.data || [])[0] || null;
    },
    /** a short-lived link to view the stored file (the packet's bucket and rules) */
    async viewUrl(path) {
      const { data, error } = await supabase.storage.from('onboarding-docs').createSignedUrl(path, 60);
      if (error) throw new Error(`The file could not be opened: ${error.message}`);
      return data.signedUrl;
    },
  };
}
